import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import BackIcon from '@mui/icons-material/ArrowBack';
import Toolbar from '@mui/material/Toolbar';
import Spacings from '~/modules/ui-kit/spacings';
import { useFetchFolderContent } from '~/hooks/api/useFetchFolderContent';
import { useUploadDocument } from '~/hooks/api/useUploadDocument';
import ContentView from '~/components/ContentView';
import DocumentForm from '~/components/DocumentForm';
import messages from './messages';
import styles from './styles.module.css';

const route = getRouteApi('/documents/$folderId/newDocument');

export const DocumentCreate = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = route.useParams();
  const search = route.useSearch();

  const folderId = params.folderId;
  const { data: created, isLoading } = useFetchFolderContent(folderId);

  const handleBackButton = () => {
    navigate({ to: search.from || '/documents' });
  };

  const createDocumentMutation = useUploadDocument({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveDocumentSuccess),
      });
      handleBackButton();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveDocumentFailure),
      });
    },
  });

  const getFolderName = () => {
    if (isLoading) {
      return null;
    }
    if (folderId === 'root') {
      return intl.formatMessage(messages.rootFolder);
    }
    return created?.folder?.name;
  };

  const handleSubmit = (values: { file: File }) => {
    createDocumentMutation.mutate({
      folderId: folderId,
      file: values.file,
    });
  };

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Spacings.Inline alignItems="center">
              <IconButton
                color="inherit"
                onClick={handleBackButton}
                size="large"
              >
                <BackIcon data-testid="back" />
              </IconButton>
              <div style={{ marginLeft: theme.spacing(2) }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </div>
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={createDocumentMutation.isPending}
    >
      <Spacings.Stack scale="xl" className={styles.form}>
        <div>
          <Typography
            variant="caption"
            sx={{
              color: 'inherit',
            }}
          >
            <FormattedMessage {...messages.currentFolder} />
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'inherit',
            }}
          >
            {getFolderName()}
          </Typography>
        </div>
        <div>{isLoading ? null : <DocumentForm onSubmit={handleSubmit} />}</div>
      </Spacings.Stack>
    </ContentView>
  );
};

export default DocumentCreate;
